import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { AuthContextProvider } from '@/hooks/use-auth';
import { ReactQueryProvider } from '@/providers/react-query-provider';
import { MarketPlaceHeader } from '@/features/marketplace/components/header';
import { Footer } from '@/features/marketplace/components/footer';
import { PADDING_TOP_HEADER_HEIGHT } from '@/constants';
import { composeClassName } from '@/utils/util_helpers';
import ImageGallery from './components/ImageGallery';
import HouseTabs from '@/features/marketplace/coming-soon/components/house-tabs';
import NewsletterModal from '@/features/marketplace/coming-soon/components/newsletter-modal';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useNewsletterSubscription, NEWSLETTER_SOURCE } from '@/hooks/use-newsletter-subscription';
import SubscriptionSuccessModal from '../../../../../components/SubscriptionSuccessModal';
import { useLocale } from '@/hooks/use-locale';
import SubscriptionButton from './components/SubscriptionButton';
import { format } from 'date-fns';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { HeaderVisibilityProvider } from '@/contexts/header-visibility-context';
import { isBrowser } from '@/utils/util_helpers/ssr';

interface HouseData {
  id: number;
  owned_by_coloc: boolean;
  managed_by_coloc: boolean;
  description_html: string;
  about_neighbourhood_html?: string;
  images: Array<{
    src: string;
    alt: string;
  }>;
  stats: {
    bedrooms: number;
    bathrooms: number;
    toilets: number;
    livable_space: number;
    launch_date: string;
  };
  houseInfo: {
    totalRent: {
      cents: number;
      currency_iso: string;
    };
    avgContribution: {
      cents: number;
      currency_iso: string;
    };
    monthlyCharges: {
      cents: number;
      currency_iso: string;
    };
    preRegistered: number;
  };
  location: {
    latitude: number;
    longitude: number;
    cityId?: number;
  };
  amenities: Array<{
    name: {
      en: string;
      fr: string;
      nl: string;
    };
    icon_type: string;
    icon_svg: string;
  }>;
}

interface HouseDetailsProps {
  colocation_slug: string;
  city: string
}

const HouseDetails: React.FC<HouseDetailsProps> = ({ colocation_slug, city }) => {
  return (
    <ReactQueryProvider>
      <AuthContextProvider>
        <HeaderVisibilityProvider>
          <MarketPlaceHeader />
          <HouseDetailsContent colocation_slug={colocation_slug} city={city} />
          <Footer />
          <div className="mt-10"></div>
        </HeaderVisibilityProvider>
      </AuthContextProvider>
    </ReactQueryProvider>
  );
};

const HouseDetailsContent: React.FC<HouseDetailsProps> = ({ colocation_slug, city }) => {
  const [houseData, setHouseData] = useState<HouseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const currentUser = useCurrentUser();
  const { currentLocale } = useLocale();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { subscribe, unsubscribe, isLoading: isSubscribing } = useNewsletterSubscription();
  const { t } = useTranslation();
  const { screenIsLargeOrEqualThan } = useWindowDimensions();
  const isMobile = !screenIsLargeOrEqualThan('sm');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (loading) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress((old) => (old < 95 ? old + 2 : old));
      }, 20);
      return () => clearInterval(interval);
    } else {
      setProgress(100);
    }
  }, [loading]);

  useEffect(() => {
    const fetchHouseData = async () => {
      try {
        // Get current locale from URL path segments
        const pathSegments = window.location.pathname.split('/');
        // Find the locale segment (en, fr, or nl)
        const locale = pathSegments.find((segment) => ['en', 'fr', 'nl'].includes(segment)) || 'en';
        const response = await fetch(`/${locale}/${colocation_slug}/details.json`);
        const { data } = await response.json();
        setHouseData(data);
      } catch (error) {
        console.error('Error fetching house data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (colocation_slug) {
      fetchHouseData();
    }
  }, [colocation_slug]);

  const handleSubscribe = async () => {
    if (isSubscribing) return; // a double click would send a second Lead

    if (!currentUser && !isSubscribed) {
      setIsNewsletterModalOpen(true);
      return;
    }

    try {
      if (isSubscribed) {
        const subscribedEmail = isBrowser() ? sessionStorage.getItem('subscribed_email') : null;
        await unsubscribe({
          email: subscribedEmail || currentUser?.email || '',
          cityId: houseData?.location?.cityId,
          houseId: houseData?.id,
        });
        setIsSubscribed(false);

        if (isBrowser()) {
          sessionStorage.removeItem('subscribed_email');
        }
      } else {
        if (!currentUser) return;

        await subscribe({
          email: currentUser.email,
          firstName: currentUser.first_name,
          lastName: currentUser.last_name,
          cityId: houseData?.location?.cityId,
          houseId: houseData?.id,
          language: currentLocale,
          source: NEWSLETTER_SOURCE.HOUSE_DETAILS_DIRECT,
        });
        setIsSubscribed(true);
        setIsSuccessModalOpen(true);
      }
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleModalClose = () => {
    setIsNewsletterModalOpen(false);
  };

  const handleNewsletterSuccess = () => {
    setIsNewsletterModalOpen(false);
    setIsSubscribed(true);
    setIsSuccessModalOpen(true);
  };

  const formatLaunchDate = (dateString: string) => {
    if (!dateString) return t('js.components.house_details.no_launch_date');
    return format(new Date(dateString), 'd MMMM yyyy');
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 w-full h-[6px] z-50 bg-transparent">
        <div
          className="h-[10PX] bg-J300 transition-all duration-200"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    );
  }

  if (!houseData) {
    return (
      <div className={composeClassName('overflow-hidden', PADDING_TOP_HEADER_HEIGHT)}>
        <div className="mx-auto max-w-7xl px-4 py-8">
          {t('js.features.marketplace.house_details.not_found')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <h1 className="mb-6 text-3xl font-bold"></h1>
      <ImageGallery
        images={houseData.images}
        virtualTourAvailable={houseData.virtual_tour_url ? true : false}
        virtualTourUrl={houseData.virtual_tour_url}
        houseData={houseData}
      />
      <HouseTabs
        houseData={houseData}
        className="m-auto w-[calc(100%-theme(space.7))] max-w-[130rem] items-center justify-between"
      />
      <div
        className="z-50 shadow-lg"
        style={{
          position: 'fixed',
          bottom: isMobile ? '63px' : '134px',
          width: 'auto',
          left: '0px',
          transform: 'translateX(0)',
          transition: 'all 0.3s ease',
          zIndex: 50,
        }}
      >
        <div
          style={{
            width: isMobile ? '183px' : '290px',
            borderRadius: '0 20px 0 0',
            height: isMobile ? '24px' : '32px',
          }}
          className={composeClassName(
            'rounded-md inline-flex h-6 items-center bg-J300 text-white',
            isMobile ? 'px-2' : 'px-3'
          )}
        >
          <IconSaxIcon
            name="badge"
            size={isMobile ? 20 : 24}
            strokeColor="none"
            strokeWidth={1.5}
          />
          <span className={composeClassName('ml-1 font-sans text-[12px] font-semibold text-white')}>
            {houseData.owned_by_coloc
              ? t('js.components.house_details.owned_and_managed_by')
              : t('js.components.house_details.managed_by')}
          </span>
        </div>
      </div>
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white py-3 shadow-longShadow sm:p-4"
        style={{ position: 'fixed', bottom: 0, width: '100%' }}
      >
        <div
          style={{ justifyContent: 'space-around' }}
          className="m-auto flex w-[calc(100%-theme(space.7))] max-w-[130rem] items-center justify-between sm:w-[80%]"
        >
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <p
                className={composeClassName(
                  'font-sans font-medium text-N700',
                  isMobile ? 'text-[11px]' : 'text-[16px]'
                )}
              >
                {t('js.components.house_details.launch_date')}
              </p>
              {houseData.stats.launch_date && !isMobile && (
                <Box
                  sx={{
                    padding: { xs: '3px 6px', sm: '6px 16px' },
                    borderRadius: 999,
                    minWidth: '100px',
                    background:
                      'linear-gradient(22.08deg, #F71818 -22.56%, #F9441B -9.98%, #FB7B1E 14.62%, #FEC600 58.21%, #FECA06 66.97%, #FED50E 75.17%, #FEE916 83.15%, #FFFF25 89.78%)',
                  }}
                  className="font-sofia-pro text-[14px] font-medium leading-[21px] text-N900"
                >
                  {t('js.components.house_card.coming_soon')}
                </Box>
              )}
            </div>
            <h3
              className={composeClassName(
                'font-sans font-bold',
                isMobile ? 'text-[16px]' : 'text-[25px]'
              )}
            >
              {formatLaunchDate(houseData.stats.launch_date)}
            </h3>
            {!isMobile && (
              <p
                className={composeClassName(
                  'font-sans font-medium text-N900',
                  isMobile ? 'text-[12px]' : 'text-[16px]'
                )}
              >
                {t('js.components.house_details.subscribe_message')}
              </p>
            )}
          </div>
          <SubscriptionButton
            onSubscribe={handleSubscribe}
            isSubscribed={isSubscribed}
            currentUser={currentUser}
          />
        </div>
      </div>

      <NewsletterModal
        isOpen={isNewsletterModalOpen}
        onClose={handleModalClose}
        onSuccess={handleNewsletterSuccess}
        houseId={houseData.id}
        city={city}
        displayPhoneNumber={true}
        phoneRequired={false}
        source={NEWSLETTER_SOURCE.HOUSE_DETAILS_MODAL}
      />
      <SubscriptionSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
      />
    </div>
  );
};

export default HouseDetails;
